import type * as api from '@gitbook/api-client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { PaletteAtomButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    DialogRoot,
    SidebarItemIcon,
    useDialogTriggerState,
    useSidebarItem,
} from '@gitbook/spine-react';

import { CreateSiteDialog, OrganizationIcon } from '../../Components';
import { SiteSortBy, useSortSites } from '../../Components/Sites';
import { useGetSettingsPalette } from './SettingsPalette';

/**
 * Sidebar button that opens the organization settings palette.
 */
export function OrganizationButton(props: {
    organization: api.Organization;
    organizationContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
    iconOnly?: boolean;
}) {
    const { iconOnly, organization, organizationContent, screen } = props;
    const createSiteDialogTrigger = useDialogTriggerState();
    const { sortedSites } = useSortSites({
        allSites: organizationContent.sites,
        organizationId: organization.id,
        sortBy: SiteSortBy.Viewed,
    });

    const palette = useGetSettingsPalette({
        organization,
        organizationContent: {
            ...organizationContent,
            sites: sortedSites.map(({ item }) => item),
        },
        onCreateSite: organization.permissions.createContent
            ? createSiteDialogTrigger.open
            : undefined,
        screen,
    });
    const { itemProps } = useSidebarItem({
        'data-testid': 'sidebar.orgButton',
        size: 'large',
        icon: <OrganizationIcon organization={organization} />,
        label: organization.title,
        iconOnly,
        className: iconOnly ? 'rounded-md' : undefined,
        elementType: 'button',
        title: iconOnly ? organization.title : undefined,
        children: <SidebarItemIcon icon={Icon.ChevronDown} />,
    });

    return (
        <>
            <PaletteAtomButton palette={palette} {...itemProps} className={itemProps.className} />
            {organization.permissions.createContent ? (
                <DialogRoot {...createSiteDialogTrigger.rootProps}>
                    <CreateSiteDialog organizationId={organization.id} />
                </DialogRoot>
            ) : null}
        </>
    );
}
